import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Card,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

interface ContactFormData {
  companyName: string;
  industry: string;
  companySize: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
  businessRequirements: string;
  passTypes: string[];
  estimatedVolume: string;
  timeline: string;
  requestDemo: boolean;
  message: string;
}

const industries = [
  'Maloprodaja / Retail',
  'Hotelijerstvo / Hospitality',
  'Fitness / Wellness',
  'Ugostiteljstvo / Restaurants',
  'Obrazovanje / Education',
  'Zdravstvo / Healthcare',
  'Transport / Transportation',
  'Događaji / Events',
  'Finansije / Finance',
  'Ostalo / Other'
];

const companySizes = [
  '1-10 zaposlenih',
  '11-50 zaposlenih',
  '51-200 zaposlenih',
  '201-1000 zaposlenih',
  '1000+ zaposlenih'
];

const passTypeOptions = [
  'Članstvo / Loyalty Kartice',
  'Kuponi',
  'Ulaznice',
  'Ostalo'
];

const volumeOptions = [
  'Do 1,000 kartica mesečno',
  '1,000 - 10,000 kartica mesečno',
  '10,000 - 100,000 kartica mesečno',
  '100,000+ kartica mesečno'
];

const timelineOptions = [
  'Odmah',
  'U narednih 30 dana',
  'U narednih 3 meseca',
  'U narednih 6 meseci',
  'Planiram za budućnost'
];

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    companyName: '',
    industry: '',
    companySize: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    email: '',
    phone: '',
    businessRequirements: '',
    passTypes: [],
    estimatedVolume: '',
    timeline: '',
    requestDemo: false,
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Naziv kompanije je obavezan';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ime je obavezno';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Prezime je obavezno';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email je obavezan';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      newErrors.email = 'Email format nije valjan';
    }

    if (!formData.industry) {
      newErrors.industry = 'Industrija je obavezna';
    }

    if (!formData.businessRequirements.trim()) {
      newErrors.businessRequirements = 'Opis poslovnih potreba je obavezan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Here you would send the form data to your backend
      // For now, we'll simulate the API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock API call would be something like:
      // await contactAPI.submitForm(formData);

      setSubmitted(true);
    } catch (error) {
      logger.error('Error submitting form:', error);
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ContactFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleSelectChange = (field: keyof ContactFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handlePassTypesChange = (event: any) => {
    setFormData({
      ...formData,
      passTypes: event.target.value
    });
  };

  if (submitted) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 4
      }}>
        <Container maxWidth="md">
          <Card sx={{
            textAlign: 'center',
            py: 6,
            px: 4,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
          }}>
            <CheckCircleIcon sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2
            }} />
            <Typography variant="h4" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
              Uspešno poslato!
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Hvala vam na interesovanju za OmniPass
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Vaš zahtev je uspešno primljen. Naš tim će vas kontaktirati u najkraćem mogućem roku
              sa detaljnim informacijama o tome kako OmniPass može da pomogne vašem biznisu.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2
              }}
            >
              Nazad na početnu
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      py: 4,
      // Prevent layout shift when dropdowns open
      overflowX: 'hidden',
      '& .MuiPaper-root': {
        // Ensure dropdown papers don't cause overflow
        maxWidth: '100%'
      }
    }}>
      <Container maxWidth="lg" sx={{
        // Prevent any overflow that could cause layout shifts
        overflowX: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 2
          }}>
            Kontaktirajte nas
          </Typography>
          <Typography variant="h5" sx={{
            color: 'text.secondary',
            maxWidth: 800,
            mx: 'auto',
            mb: 3
          }}>
            Zainteresovani ste za OmniPass? Popunite formu ispod i naš tim će vas kontaktirati
            sa personalizovanom ponudom za vaše poslovne potrebe.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Main Contact Form */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{
              p: 4,
              borderRadius: 3,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
            }}>
              <form onSubmit={handleSubmit}>
                {loading && <LinearProgress sx={{ mb: 3 }} />}

                {/* Company Information */}
                <Typography variant="h6" gutterBottom sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  color: 'primary.main',
                  fontWeight: 600
                }}>
                  <BusinessIcon /> Informacije o kompaniji
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Naziv kompanije *"
                      value={formData.companyName}
                      onChange={handleInputChange('companyName')}
                      error={!!errors.companyName}
                      helperText={errors.companyName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth error={!!errors.industry}>
                      <InputLabel>Industrija *</InputLabel>
                      <Select
                        value={formData.industry}
                        onChange={handleSelectChange('industry')}
                        label="Industrija *"
                        MenuProps={{
                          disableScrollLock: true,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                            },
                          },
                        }}
                      >
                        {industries.map((industry) => (
                          <MenuItem key={industry} value={industry}>
                            {industry}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Veličina kompanije</InputLabel>
                      <Select
                        value={formData.companySize}
                        onChange={handleSelectChange('companySize')}
                        label="Veličina kompanije"
                        MenuProps={{
                          disableScrollLock: true,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                            },
                          },
                        }}
                      >
                        {companySizes.map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Contact Person */}
                <Typography variant="h6" gutterBottom sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  color: 'primary.main',
                  fontWeight: 600
                }}>
                  <PersonIcon /> Kontakt osoba
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Ime *"
                      value={formData.firstName}
                      onChange={handleInputChange('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Prezime *"
                      value={formData.lastName}
                      onChange={handleInputChange('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Pozicija u kompaniji"
                      value={formData.jobTitle}
                      onChange={handleInputChange('jobTitle')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Email adresa *"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      error={!!errors.email}
                      helperText={errors.email}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* Business Requirements */}
                <Typography variant="h6" gutterBottom sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  color: 'primary.main',
                  fontWeight: 600
                }}>
                  <EmailIcon /> Poslovne potrebe
                </Typography>

                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Opišite vaše poslovne potrebe *"
                      value={formData.businessRequirements}
                      onChange={handleInputChange('businessRequirements')}
                      error={!!errors.businessRequirements}
                      helperText={errors.businessRequirements || 'Opišite kako planirate da koristite digitalne kartice'}
                      placeholder="Na primer: Potrebne su nam loyalty kartice za naše klijente u maloprodajnim objektima..."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Tipovi kartica (više opcija)</InputLabel>
                      <Select
                        multiple
                        value={formData.passTypes}
                        onChange={handlePassTypesChange}
                        label="Tipovi kartica (više opcija)"
                        renderValue={(selected) => (selected as string[]).join(', ')}
                        MenuProps={{
                          disableScrollLock: true,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                            },
                          },
                        }}
                      >
                        {passTypeOptions.map((type) => (
                          <MenuItem key={type} value={type}>
                            <Checkbox checked={formData.passTypes.indexOf(type) > -1} />
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Procenjeni obim</InputLabel>
                      <Select
                        value={formData.estimatedVolume}
                        onChange={handleSelectChange('estimatedVolume')}
                        label="Procenjeni obim"
                        MenuProps={{
                          disableScrollLock: true,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                            },
                          },
                        }}
                      >
                        {volumeOptions.map((volume) => (
                          <MenuItem key={volume} value={volume}>
                            {volume}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Planiran početak</InputLabel>
                      <Select
                        value={formData.timeline}
                        onChange={handleSelectChange('timeline')}
                        label="Planiran početak"
                        MenuProps={{
                          disableScrollLock: true,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                            },
                          },
                        }}
                      >
                        {timelineOptions.map((timeline) => (
                          <MenuItem key={timeline} value={timeline}>
                            {timeline}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Dodatne napomene"
                      value={formData.message}
                      onChange={handleInputChange('message')}
                      placeholder="Bilo kakve dodatne informacije, specifični zahtevi, pitanja..."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.requestDemo}
                          onChange={(e) => setFormData({
                            ...formData,
                            requestDemo: e.target.checked
                          })}
                        />
                      }
                      label="Želim da zakažem demo prezentaciju"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/')}
                    disabled={loading}
                  >
                    Otkaži
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={<SendIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2
                    }}
                  >
                    {loading ? 'Šalje se...' : 'Pošalji zahtev'}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>

          {/* Sidebar with Info */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <Card sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                mb: 3
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Zašto OmniPass?
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                  <li>Podrška za Apple i Google Wallet</li>
                  <li>Profesionalni B2B portal</li>
                  <li>Detaljne analitike i izveštaji</li>
                  <li>Sigurnost na enterprise nivou</li>
                  <li>24/7 tehnička podrška</li>
                  <li>Jednostavna integracija</li>
                </Box>
              </Card>

              <Card sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #0a1525 0%, #14264a 100%)',
                color: 'white'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Potrebna pomoć?
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Naš tim je tu da odgovori na sva vaša pitanja o OmniPass platformi.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2">info@omnipass.rs</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2">+381 11 123 4567</Typography>
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactPage;
